import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { useCallback } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { RootState } from 'types/state'
import { DISTRIBUTOR_BRAND_FILTER_KEY } from '../distributorBrand/DistributorBrandFilterNodeFactory'
import { MANUFACTURER_FILTER_KEY } from '../manufacturer/ManufacturerFilter'
import type { FilterComponentRendererType, FilterGenericPropsType, FilterKeysType, OptionGenericType } from '../type'
import { DISTRIBUTOR_FILTER_KEY } from './DistributorFilterNodeFactory'

export type DistributorFilterOptionsType = OptionGenericType<string>[]

const defaultValue = 'all'

type DistributorFilterProps = FilterGenericPropsType & {
  distributors: HardwareSupplierFilterKeyType[]
  RendererComponent: FilterComponentRendererType<DistributorFilterOptionsType>
  filterKey: FilterKeysType
}

const DistributorFilter = ({
  filterKey,
  label,
  modifiableFilterValues,
  persistentFilterValues,
  allFilterValues,
  setModifiableFilters,
  distributors,
  RendererComponent,
}: DistributorFilterProps) => {
  const translate = useTranslate()
  const zone = useSelector((state: RootState) => state.zone)
  const { getSupplierConfig } = useGetSupplierConfig()

  const getDistributorsOptions = useCallback(
    (enabledDistributors: HardwareSupplierFilterKeyType[]) => {
      const options = [{ id: 'default', title: translate('All'), value: defaultValue }]
      enabledDistributors.forEach((distributor) => {
        const config = getSupplierConfig(distributor)

        if (!config) return
        options.push({
          id: `require_${config.filterKey}`,
          title: config.name || '',
          value: config.filterKey,
        })
      })
      return options
    },
    [getSupplierConfig]
  )

  const onSelect = useCallback(
    (value) => {
      const newFilterValues = { ...modifiableFilterValues, [filterKey]: value }
      if (value === undefined || value === defaultValue) {
        delete newFilterValues[DISTRIBUTOR_FILTER_KEY]
      }

      // clear manufacturer filters
      delete newFilterValues[MANUFACTURER_FILTER_KEY]
      delete newFilterValues[DISTRIBUTOR_BRAND_FILTER_KEY]

      setModifiableFilters(newFilterValues)
    },
    [modifiableFilterValues]
  )

  // Special request disable distributor filter in hardware section
  const disabled = zone !== 'crm'
  // Have to map from false to 'all'
  const selectedValue = allFilterValues[filterKey] === false ? 'all' : allFilterValues[filterKey]
  return (
    <RendererComponent
      id={filterKey}
      defaultValue={defaultValue}
      label={label || translate('Available from')}
      options={getDistributorsOptions(distributors)}
      disabled={disabled}
      onSelect={onSelect}
      value={selectedValue}
    />
  )
}

export default DistributorFilter
